/**
 * AcceptTemplate - Displayed when a user navigates to /join/t_<code>
 * 
 * Resolves the template invite code, shows admin name/email and template name,
 * and allows the user to add the template to their linked templates list.
 */

import React, { useState, useEffect, useContext } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  CircularProgress,
  Alert,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PersonIcon from '@mui/icons-material/Person';
import DescriptionIcon from '@mui/icons-material/Description';
import { AuthContext } from '@healthdesk/shared-hooks';
import { db } from '@healthdesk/shared-services';
import { chartmindAdminService } from '../../../../services/chartmind';
import firebase from 'firebase/compat/app';

export default function AcceptTemplate({ templateCode }) {
  const { user } = useContext(AuthContext);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [alreadyLinked, setAlreadyLinked] = useState(false);
  const [templateInfo, setTemplateInfo] = useState(null); // { admin, template }

  useEffect(() => {
    let cancelled = false;
    async function resolve() {
      if (!templateCode) {
        setError('Invalid Patient Encounter Template link.');
        setLoading(false);
        return;
      }
      try {
        const result = await chartmindAdminService.findTemplateByInviteCode(templateCode);
        if (cancelled) return;
        if (!result) {
          setError('Patient Encounter Template not found. The link may be invalid or expired.');
          setLoading(false);
          return;
        }
        setTemplateInfo(result);

        // Check if already linked
        if (user?.uid) {
          const userDoc = await db.collection('users').doc(user.uid).get();
          const linked = userDoc.data()?.linkedTemplates || [];
          const exists = linked.some(
            (lt) => lt.adminId === result.admin.id && lt.templateId === result.template.id
          );
          if (exists) setAlreadyLinked(true);
        }
      } catch (err) {
        console.error('[AcceptTemplate] resolve error:', err);
        if (!cancelled) setError('Failed to resolve Patient Encounter Template link.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    resolve();
    return () => { cancelled = true; };
  }, [templateCode, user?.uid]);

  const handleAdd = async () => {
    if (!user?.uid || !templateInfo) return;
    setSaving(true);
    setError(null);

    try {
      const { admin, template } = templateInfo;
      const entry = {
        adminId: admin.id,
        adminName: admin.name || admin.facilityName || admin.email || 'Unknown',
        adminEmail: admin.email || '',
        templateId: template.id,
        templateName: template.name || 'Untitled Template',
        linkedAt: new Date().toISOString(),
      };

      await db.collection('users').doc(user.uid).update({
        linkedTemplates: firebase.firestore.FieldValue.arrayUnion(entry),
      });

      setSuccess(true);
      setAlreadyLinked(true);
    } catch (err) {
      console.error('[AcceptTemplate] add error:', err);
      setError('Failed to add Patient Encounter Template. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" minHeight="40vh" p={3}>
        <CircularProgress />
        <Typography sx={{ mt: 2 }}>Looking up template...</Typography>
      </Box>
    );
  }

  if (error && !templateInfo) {
    return (
      <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" minHeight="40vh" p={3}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (!user) {
    return (
      <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" minHeight="40vh" p={3}>
        <Alert severity="warning">Please sign in to add this ChartMind Patient Encounter Template.</Alert>
      </Box>
    );
  }

  const { admin, template } = templateInfo || {};

  return (
    <Box sx={{ maxWidth: 520, mx: 'auto', py: 4, px: 2 }}>
      <Typography variant="h5" gutterBottom>
        Add ChartMind Patient Encounter Template
      </Typography>

      <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
        {/* Admin info */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <PersonIcon sx={{ mr: 1, color: 'text.secondary' }} />
          <Box>
            <Typography variant="subtitle2" color="text.secondary">
              Created by
            </Typography>
            <Typography variant="body1">
              {admin?.name || admin?.facilityName || 'Unknown Admin'}
            </Typography>
            {admin?.email && (
              <Typography variant="body2" color="text.secondary">
                {admin.email}
              </Typography>
            )}
          </Box>
        </Box>

        {/* Encounter profile info */}
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <DescriptionIcon sx={{ mr: 1, color: 'text.secondary' }} />
          <Box>
            <Typography variant="subtitle2" color="text.secondary">
              Template
            </Typography>
            <Typography variant="body1">
              {template?.name || 'Untitled Template'}
            </Typography>
          </Box>
        </Box>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} icon={<CheckCircleIcon />}>
          Patient Encounter Template added successfully! You can now select it from your ChartMind dashboard.
        </Alert>
      )}

      {alreadyLinked && !success && (
        <Alert severity="info" sx={{ mb: 2 }}>
          You already have this Patient Encounter Template linked to your account.
        </Alert>
      )}

      {!alreadyLinked && !success && (
        <Button
          variant="contained"
          fullWidth
          size="large"
          onClick={handleAdd}
          disabled={saving}
          startIcon={saving ? <CircularProgress size={20} /> : null}
        >
          {saving ? 'Adding...' : 'Add Template to My List'}
        </Button>
      )}
    </Box>
  );
}
